import { RichUtils, EditorState, ContentBlock, genKey, Modifier } from "draft-js";
import Immutable from 'immutable';
import { createToolbarButtons } from "../createToolbarButtons";
import { createHeadingButton } from "./HeadingBlockButton";
import { BlockquoteBlockButton } from "./BlockquoteBlockButton";
import { CodeBlockButton } from "./CodeBlockButton";
import { createListBlockButton } from "./ListBlockButton";
import { insertBlock } from "lib/helpers";

const defaultOptions = {
	buttons: ['h1', 'h2', 'h3', 'spliter', 'blockquote', 'codeblock', 'ol', 'ul'],
	// block breackout 实现参考
	// @see https://github.com/icelab/draft-js-block-breakout-plugin/blob/master/src/index.js
	breakoutBlockType: 'unstyled',
	breakoutBlocks: [
		'header-one',
		'header-two',
		'header-three',
		'header-four',
		'header-five',
		'header-six',
		'table',
		'atomic',
	],
	doubleBreakoutBlocks: [
		'blockquote',
		'unordered-list-item',
		'ordered-list-item',
		'code-block',
	],
};

const BUTTON_MAP = {
	h1: createHeadingButton('h1'),
	h2: createHeadingButton('h2'),
	h3: createHeadingButton('h3'),
	h4: createHeadingButton('h4'),
	h5: createHeadingButton('h5'),
	h6: createHeadingButton('h6'),
	blockquote: BlockquoteBlockButton,
	codeblock: CodeBlockButton,
	ol: createListBlockButton(true),
	ul: createListBlockButton(false),
};

/**
 * @returns {DraftEditorPlugin}
 */
export function createBlockStylePlugin(options = defaultOptions) {
	options = { ...defaultOptions, ...options };

	return {
		name: 'block-style',

		getToolbarButtons(props) {
			if (props.env === 'root') {
				return createToolbarButtons(BUTTON_MAP, options.buttons, props);
			}
			return [];
		},

		handleCommand({ getEditorState, setEditorState }, command, evt) {
			const editorState = getEditorState();
			if (command === 'return') {
				if (evt.shiftKey) {
					return setEditorState(RichUtils.insertSoftNewline(editorState)), 'handled';
				} else {
					const breakoutEditorState = breackoutBlock(options, editorState, evt);
					if (breakoutEditorState) {
						return setEditorState(breakoutEditorState), 'handled';
					}
				}
			}
			if (command === 'tab') {
				const handledEditorState = RichUtils.onTab(evt, editorState, 6);
				if (handledEditorState !== editorState) {
					return setEditorState(handledEditorState), 'handled';
				}
			}
			if (command === 'backspace') {
				const content = editorState.getCurrentContent();
				const selection = editorState.getSelection();
				if (selection.isCollapsed()) {
					const currentBlockKey = selection.getFocusKey();
					const currentBlock = content.getBlockForKey(currentBlockKey);
					if (currentBlock && !currentBlock.getLength() && currentBlock.getType() !== 'unstyled') {
						setEditorState(
							EditorState.push(
								editorState,
								Modifier.setBlockType(content, selection, 'unstyled'),
								'change-block-type'
							)
						);
						return 'handled';
					}
				}
			}
			return 'not-handled';
		}
	}
}

/**
 * 控制回车后切换块类型为默认类型，如输入标题（heading-two）回车后输入普通段落（unstyled），而不是继续输入标题
 * @param {*} options
 * @param {EditorState} editorState
 * @param {React.KeyboardEvent} evt
 * @see https://github.com/icelab/draft-js-block-breakout-plugin/blob/master/src/index.js
 */
function breackoutBlock(options, editorState, evt) {
	const { breakoutBlocks, doubleBreakoutBlocks, breakoutBlockType } = options;
	const currentBlockType = RichUtils.getCurrentBlockType(editorState);
	const selection = editorState.getSelection()
	const isSingleBreakoutBlock = breakoutBlocks.indexOf(currentBlockType) > -1;
	const isDoubleBreakoutBlock = doubleBreakoutBlocks.indexOf(currentBlockType) > -1;

	if (currentBlockType === 'atomic' && selection.getAnchorKey() === selection.getFocusKey()) {
		return insertBlock(editorState, selection.getFocusKey(), 'after');
	}

	// Does the current block type match a type we care about?
	if (isSingleBreakoutBlock || isDoubleBreakoutBlock) {

		// Check if the selection is collapsed
		if (selection.isCollapsed()) {
			const contentState = editorState.getCurrentContent();
			const currentBlock = contentState.getBlockForKey(selection.getEndKey());
			const endOffset = selection.getEndOffset();
			const atEndOfBlock = (endOffset === currentBlock.getLength());
			const atStartOfBlock = (endOffset === 0);

			// Check we’re at the start/end of the current block
			if ((atEndOfBlock && isSingleBreakoutBlock)
				|| (atStartOfBlock && isSingleBreakoutBlock)
				|| (atStartOfBlock && !currentBlock.getLength()
				|| ((atStartOfBlock || atEndOfBlock) && (evt.metaKey || evt.ctrlKey)))
			) {
				const emptyBlockKey = genKey()
				const emptyBlock = new ContentBlock({
					key: emptyBlockKey,
					text: '',
					type: breakoutBlockType,
					characterList: Immutable.List(),
					depth: 0,
				})
				const blockMap = contentState.getBlockMap()
				// Split the blocks
				const blocksBefore = blockMap.toSeq().takeUntil(function (v) {
					return v === currentBlock
				});

				const blocksAfter = blockMap.toSeq().skipUntil(function (v) {
					return v === currentBlock
				}).rest();

				let augmentedBlocks;
				let focusKey;
				// Choose which order to apply the augmented blocks in depending
				// on whether we’re at the start or the end
				if (atEndOfBlock) {
					if (isDoubleBreakoutBlock && !currentBlock.getLength()) {
						// Discard Current as it was blank
						augmentedBlocks = [
							[emptyBlockKey, emptyBlock],
						];
					} else {
						// Current first, empty block afterwards
						augmentedBlocks = [
							[currentBlock.getKey(), currentBlock],
							[emptyBlockKey, emptyBlock],
						];
					}
					focusKey = emptyBlockKey;
				} else {
					// Empty first, current block afterwards
					augmentedBlocks = [
						[emptyBlockKey, emptyBlock],
						[currentBlock.getKey(), currentBlock],
					];
					focusKey = currentBlock.getKey();
				}
				// Join back together with the current + new block
				const newBlocks = blocksBefore.concat(augmentedBlocks, blocksAfter).toOrderedMap()
				const newContentState = contentState.merge({
					blockMap: newBlocks,
					selectionBefore: selection,
					selectionAfter: selection.merge({
						anchorKey: focusKey,
						anchorOffset: 0,
						focusKey: focusKey,
						focusOffset: 0,
						isBackward: false,
					}),
				});
				// return new state
				return EditorState.push(editorState, newContentState, 'split-block');
			}
		}
	}
	return null;
}



// WEBPACK FOOTER //
// ./lib/editor/DraftEditor/plugins/blockStylePlugin/createBlockStylePlugin.js